import { KpiCategoryResponse, KpiDefinitionResponse, KpiMasterResponse, VendorSpecificSettingsApiResponse } from '../../models/vsc-settings.model';
import {
  CategoryCardInput,
  KPIFrequency,
  KPITrendComponentInput,
  KpiCategoryScoreData,
  KpiScoreData,
  KpiScoreWeeklyData,
  ScorecardPeriod,
  TrendsDataPoint,
  WeeklyScorecardPeriod,
} from '../../models/vsc-scorecard.model';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_DISPLAY = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Public contracts ──────────────────────────────────────────────────────────

export interface ScorecardStaticData {
  vendorSettings: VendorSpecificSettingsApiResponse;
  kpiMaster: KpiMasterResponse[];
  kpiCategories: KpiCategoryResponse[];
}

export interface ScorecardScoreData {
  categoryScores: KpiCategoryScoreData[];
  kpiScores: KpiScoreData[];
}

export interface WeeklyScoreData {
  categoryScores: KpiCategoryScoreData[];
  weeklyKpiScores: KpiScoreWeeklyData[];
  monthlyKpiScores: KpiScoreData[]; // scores (out-of-five / weighted) come from the monthly API
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Parses "Jul 2026" (or "Sept 2026") → { month: "jul", year: 2026 } */
export function parseMonthLabel(label: string): { month: string; year: number } {
  const parts = label.trim().split(/\s+/);
  return {
    month: parts[0].substring(0, 3).toLowerCase(),
    year: Number.parseInt(parts.at(-1)!, 10),
  };
}

/**
 * Returns 16 periods: 8 current-year months ending at [month, year]
 * followed by the same 8 months one year prior.
 */
export function buildScorecardPeriods(month: string, year: number): ScorecardPeriod[] {
  const idx = MONTHS.indexOf(month);
  const curr: ScorecardPeriod[] = [];
  for (let i = 7; i >= 0; i--) {
    let m = idx - i;
    let y = year;
    if (m < 0) { m += 12; y--; }
    curr.push({ month: MONTHS[m], year: y });
  }
  return [...curr, ...curr.map(p => ({ month: p.month, year: p.year - 1 }))];
}

/**
 * Returns 16 week-periods: 8 current-year weeks ending at [week, year]
 * followed by the same 8 week numbers one year prior.
 */
export function buildWeeklyScorecardPeriods(week: number, year: number): WeeklyScorecardPeriod[] {
  const curr: WeeklyScorecardPeriod[] = [];
  for (let i = 7; i >= 0; i--) {
    let w = week - i;
    let y = year;
    if (w < 1) { w += 52; y--; }
    curr.push({ week: w, year: y });
  }
  return [...curr, ...curr.map(p => ({ week: p.week, year: p.year - 1 }))];
}

// ── Private mapping helpers ───────────────────────────────────────────────────

function buildFooterText(master: KpiMasterResponse, catKpi: KpiDefinitionResponse | undefined): string {
  if (!catKpi) return '';
  const band5 = catKpi.scoreBands.find(b => b.score === 5);
  if (!band5) return '';
  const value = master.isLowerBetter ? band5.max : band5.min;
  if (value === null || value === undefined) return '';
  const op = master.isLowerBetter ? '≤' : '≥';
  const shortDesc = master.description.split('.')[0].trim();
  const unit = master.unit && master.unit !== 'none' ? ` ${master.unit}` : '';
  return `Score 5: ${shortDesc} ${op} ${value}${unit}`;
}

function buildTrends(
  kpiId: number,
  currentPeriods: ScorecardPeriod[],
  kpiScores: KpiScoreData[],
): TrendsDataPoint[] {
  return currentPeriods.map(p => {
    const curr = kpiScores.find(d => d.kpiId === kpiId && d.month === p.month && d.year === p.year);
    const prior = kpiScores.find(d => d.kpiId === kpiId && d.month === p.month && d.year === p.year - 1);
    const mIdx = MONTHS.indexOf(p.month);
    return {
      month: MONTH_DISPLAY[mIdx] ?? p.month,
      trendsOutOfFiveDataThisYear: curr?.outOfFiveScore ?? null,
      trendsOutOfFiveDataPreviousYear: prior?.outOfFiveScore ?? null,
      trendsWeightedDataThisYear: curr?.weightedScore ?? null,
      trendsWeightedDataPreviousYear: prior?.weightedScore ?? null,
      trendsMeanValuesThisYear: curr?.meanValue ?? null,
      trendsMeanValuesPreviousYear: prior?.meanValue ?? null,
    };
  });
}

function weekToMonth(week: number, year: number): { month: string; year: number } {
  const jan4 = new Date(year, 0, 4);
  const dayInWeek = new Date(jan4.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
  return { month: MONTHS[dayInWeek.getMonth()], year: dayInWeek.getFullYear() };
}

function buildWeeklyTrends(
  kpiId: number,
  currentPeriods: WeeklyScorecardPeriod[],
  weeklyScores: KpiScoreWeeklyData[],
  monthlyScores: KpiScoreData[],
): TrendsDataPoint[] {
  return currentPeriods.map(p => {
    const curr = weeklyScores.find(d => d.kpiId === kpiId && d.week === p.week && d.year === p.year);
    const prior = weeklyScores.find(d => d.kpiId === kpiId && d.week === p.week && d.year === p.year - 1);
    const { month: currMonth, year: currYear } = weekToMonth(p.week, p.year);
    const { month: priorMonth, year: priorYear } = weekToMonth(p.week, p.year - 1);
    const currScore = monthlyScores.find(d => d.kpiId === kpiId && d.month === currMonth && d.year === currYear);
    const priorScore = monthlyScores.find(d => d.kpiId === kpiId && d.month === priorMonth && d.year === priorYear);
    return {
      month: `W${p.week}`,
      trendsOutOfFiveDataThisYear: currScore?.outOfFiveScore ?? null,
      trendsOutOfFiveDataPreviousYear: priorScore?.outOfFiveScore ?? null,
      trendsWeightedDataThisYear: currScore?.weightedScore ?? null,
      trendsWeightedDataPreviousYear: priorScore?.weightedScore ?? null,
      trendsMeanValuesThisYear: curr?.meanValue ?? null,
      trendsMeanValuesPreviousYear: prior?.meanValue ?? null,
    };
  });
}

// ── Main mapper ───────────────────────────────────────────────────────────────

export function mapToCategories(
  staticData: ScorecardStaticData,
  scoreData: ScorecardScoreData,
  selectedMonth: string,
): CategoryCardInput[] {
  const { month, year } = parseMonthLabel(selectedMonth);
  // First 8 of the 16 periods are the current-year months used for trend charts
  const currentPeriods = buildScorecardPeriods(month, year).slice(0, 8);

  const kpiMasterMap = new Map(staticData.kpiMaster.map(k => [k.id, k]));

  // categoryId → Map<kpiId, KpiDefinitionResponse> (for score bands → footer)
  const catKpiDefsMap = new Map<number, Map<number, KpiDefinitionResponse>>();
  staticData.kpiCategories.forEach(cat => {
    catKpiDefsMap.set(cat.id, new Map(cat.kpis.map(k => [k.id, k])));
  });
  const catNamesMap = new Map(staticData.kpiCategories.map(c => [c.id, c.name]));

  return staticData.vendorSettings.config.kpiCategories
    .filter(cat => cat.weight > 0 && scoreData.categoryScores.some(s => s.kpiCategoryId === cat.id))
    .map(cat => {
      // Exact month match first; fall back to latest available entry for that category
      const catScoreEntry =
        scoreData.categoryScores.find(s => s.kpiCategoryId === cat.id && s.month === month && s.year === year) ??
        [...scoreData.categoryScores].reverse().find(s => s.kpiCategoryId === cat.id);

      const kpiDefs = catKpiDefsMap.get(cat.id) ?? new Map<number, KpiDefinitionResponse>();

      const kpis: KPITrendComponentInput[] = cat.kpis
        .filter(k => k.weight > 0)
        .map(kpiSetting => {
          const master = kpiMasterMap.get(kpiSetting.id);
          return {
            kpiId: kpiSetting.id,
            kpiWeight: kpiSetting.weight,
            kpiName: master?.name ?? kpiSetting.name,
            kpiDescription: master?.description ?? '',
            kpiUnit: master?.unit ?? '',
            kpiMaxOutofFiveScore: 5,
            kpiMaxWeightedScore: kpiSetting.weight,
            trends: buildTrends(kpiSetting.id, currentPeriods, scoreData.kpiScores),
            footerText: master ? buildFooterText(master, kpiDefs.get(kpiSetting.id)) : '',
            expanded: false,
            isLowerBetter: master?.isLowerBetter ?? false,
            frequency: KPIFrequency.Monthly,
          };
        });

      return {
        categoryId: cat.id,
        categoryWeight: cat.weight,
        categoryName: catNamesMap.get(cat.id) ?? cat.name,
        outOfFiveScore: catScoreEntry?.outOfFiveScore ?? 0,
        maxOutOfFiveScore: 5,
        outOfTotalScore: catScoreEntry?.totalScore ?? 0,
        maxOutOfTotalScore: cat.weight,
        kpis,
        expanded: false,
      };
    });
}

export function mapToWeeklyCategories(
  staticData: ScorecardStaticData,
  scoreData: WeeklyScoreData,
  week: number,
  year: number,
): CategoryCardInput[] {
  const currentPeriods = buildWeeklyScorecardPeriods(week, year).slice(0, 8);

  const kpiMasterMap = new Map(staticData.kpiMaster.map(k => [k.id, k]));

  const catKpiDefsMap = new Map<number, Map<number, KpiDefinitionResponse>>();
  staticData.kpiCategories.forEach(cat => {
    catKpiDefsMap.set(cat.id, new Map(cat.kpis.map(k => [k.id, k])));
  });
  const catNamesMap = new Map(staticData.kpiCategories.map(c => [c.id, c.name]));

  return staticData.vendorSettings.config.kpiCategories
    .filter(cat => cat.weight > 0 && scoreData.categoryScores.some(s => s.kpiCategoryId === cat.id))
    .map(cat => {
      const catScoreEntry = [...scoreData.categoryScores].reverse().find(s => s.kpiCategoryId === cat.id);
      const kpiDefs = catKpiDefsMap.get(cat.id) ?? new Map<number, KpiDefinitionResponse>();

      const kpis: KPITrendComponentInput[] = cat.kpis
        .filter(k => k.weight > 0)
        .filter(k => kpiMasterMap.get(k.id)?.frequency !== 'monthly')
        .map(kpiSetting => {
          const master = kpiMasterMap.get(kpiSetting.id);
          return {
            kpiId: kpiSetting.id,
            kpiWeight: kpiSetting.weight,
            kpiName: master?.name ?? kpiSetting.name,
            kpiDescription: master?.description ?? '',
            kpiUnit: master?.unit ?? '',
            kpiMaxOutofFiveScore: 5,
            kpiMaxWeightedScore: kpiSetting.weight,
            trends: buildWeeklyTrends(kpiSetting.id, currentPeriods, scoreData.weeklyKpiScores, scoreData.monthlyKpiScores),
            footerText: master ? buildFooterText(master, kpiDefs.get(kpiSetting.id)) : '',
            expanded: false,
            isLowerBetter: master?.isLowerBetter ?? false,
            frequency: KPIFrequency.Weekly,
          };
        });

      return {
        categoryId: cat.id,
        categoryWeight: cat.weight,
        categoryName: catNamesMap.get(cat.id) ?? cat.name,
        outOfFiveScore: catScoreEntry?.outOfFiveScore ?? 0,
        maxOutOfFiveScore: 5,
        outOfTotalScore: catScoreEntry?.totalScore ?? 0,
        maxOutOfTotalScore: cat.weight,
        kpis,
        expanded: false,
      };
    })
    .filter(cat => cat.kpis.length > 0);
}
